import { cloneElement, forwardRef, ReactElement, Ref, useEffect, useLayoutEffect, useRef } from "react";
import type { TransitionProps } from "@mui/material/transitions";
import { useForkRef } from "@mui/material/utils";

import {
  EASE_ENTRANCE,
  EASE_OUT,
  EASE_SMOOTH_OUT,
  FRAME_SLACK_MS,
  MODAL_CLOSE_MS,
  MODAL_OPEN_MS,
  MODAL_SCALE,
  prefersReducedMotion,
  SHEET_CLOSE_MS,
  SHEET_OPEN_MS,
  TOOLTIP_IN_MS,
  TOOLTIP_OUT_MS,
  TOOLTIP_SCALE,
} from "../styles/motion";

// A scale + fade open/close for any MUI surface with a `TransitionComponent` slot — Popover,
// Menu, Dialog, Tooltip. `createScaleTransition` makes one per recipe; the phases, timing and
// cleanup are shared:
//
//   Open   the surface grows from `openScale` to 1 and fades in, on the project entrance curve.
//   Close  shorter and quieter — it dips to `closeScale` and fades out on --ease-smooth-out,
//          per the polish open/close asymmetry.
//
// It replaced MUI's Grow / Fade on these surfaces, which layered an unrelated curve on each and
// (for Grow) a lopsided 0.75 × 0.5625 start that reads as a zoom rather than a surface arriving.
//
// Origin: the host's own `onEntering` handler writes `transform-origin` where it has one
// (Popover positions the paper and aims the origin at `transformOrigin`; Tooltip's styles aim
// it at the arrow). It is called *before* the start pose is committed so the very first frame
// already uses the right origin. A Dialog has no handler: its container scales about its
// centre, which is where the paper sits — and it is the paper that scales (see `target`).
//
// Constraints (project motion conventions, mirroring useSwipeToDismiss):
//   - Compositor-only: opacity + transform (scale) on the node, nothing else.
//   - Imperative inline styles, so opening never re-renders the surface's subtree.
//   - One-shot `will-change`: promoted when a phase starts, cleared when it ends.
//   - Reduced motion: every phase resolves synchronously. Hosts may also pass
//     `transitionDuration={0}` (so their backdrop skips its fade too), and the timer fallback
//     below still completes a phase if the OS setting flips mid-session.
//
// Contract with MUI Modal/Popover/Popper: call onEnter → onEntering → onEntered and
// onExit → onExiting → onExited in order. Modal only unmounts the surface after `onExited`,
// which is what lets the close animate at all.

export interface ScaleTransitionRecipe {
  // Used when the host passes no timeout for the phase.
  openMs: number;
  closeMs: number;
  // The pre-open scale the surface grows from, and the scale it dips to on close.
  openScale: number;
  closeScale: number;
  openEase?: string;
  closeEase?: string;
  // The element that actually scales and fades, when it isn't the node the host hands over.
  // A Dialog hands over its full-viewport container; animating that would promote a layer the
  // size of the screen for every open, so the modal recipe aims at the paper inside it.
  target?: (node: HTMLElement) => HTMLElement | null;
}

type ScaleTransitionProps = TransitionProps & { children: ReactElement };

// Resolve `done` on the node's own opacity transitionend, with a timer fallback in
// case the event never fires (interrupted, reduced-motion CSS, display change). The
// returned function cancels without calling `done`, for when a new phase takes over.
const afterTransition = (node: HTMLElement, ms: number, done: () => void) => {
  let settled = false;
  const onEnd = (event: TransitionEvent) => {
    if (event.target === node && event.propertyName === "opacity") {
      finish();
    }
  };
  const cancel = () => {
    settled = true;
    node.removeEventListener("transitionend", onEnd);
    window.clearTimeout(timer);
  };
  const finish = () => {
    if (settled) {
      return;
    }
    cancel();
    done();
  };
  node.addEventListener("transitionend", onEnd);
  const timer = window.setTimeout(finish, ms + FRAME_SLACK_MS);
  return cancel;
};

export const createScaleTransition = (recipe: ScaleTransitionRecipe, displayName: string) => {
  const openEase = recipe.openEase ?? EASE_ENTRANCE;
  const closeEase = recipe.closeEase ?? EASE_SMOOTH_OUT;

  const resolveDuration = (timeout: TransitionProps["timeout"], phase: "enter" | "exit") => {
    if (prefersReducedMotion) {
      return 0;
    }
    if (typeof timeout === "number") {
      return timeout;
    }
    const fallback = phase === "enter" ? recipe.openMs : recipe.closeMs;
    return timeout?.[phase] ?? fallback;
  };

  const ScaleTransition = forwardRef<HTMLElement, ScaleTransitionProps>(function ScaleTransition(props, ref) {
    const {
      in: inProp = false,
      appear = true,
      children,
      timeout,
      onEnter,
      onEntering,
      onEntered,
      onExit,
      onExiting,
      onExited,
      // react-transition-group-only knobs: consumed here, never forwarded to the DOM node.
      /* eslint-disable @typescript-eslint/no-unused-vars */
      addEndListener,
      easing,
      mountOnEnter,
      unmountOnExit,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      // Everything else (Modal's tabIndex, Dialog's role, …) goes to the surface.
      ...other
    } = props as ScaleTransitionProps & { easing?: unknown; mountOnEnter?: boolean; unmountOnExit?: boolean };

    const nodeRef = useRef<HTMLElement | null>(null);
    // React 18: the child's ref lives on the element itself.
    const childRef = (children as ReactElement & { ref?: Ref<HTMLElement> }).ref;
    const handleRef = useForkRef(nodeRef, childRef, ref);

    const isFirstRun = useRef(true);
    // Which phase is in flight, so a reopen during the close retargets from the current
    // pose instead of snapping back to the start pose.
    const phase = useRef<"idle" | "entering" | "exiting">("idle");
    const cancelPhase = useRef<(() => void) | null>(null);

    // Layout effect: the start pose must be on the node before the first paint.
    useLayoutEffect(() => {
      const host = nodeRef.current;
      if (!host) {
        return;
      }
      // Styles go on `node`; MUI's callbacks always get `host`, the node it gave us.
      const node = recipe.target?.(host) ?? host;
      const isAppearing = isFirstRun.current;
      isFirstRun.current = false;

      const interruptedExit = phase.current === "exiting";
      cancelPhase.current?.();
      cancelPhase.current = null;

      if (inProp) {
        const ms = isAppearing && !appear ? 0 : resolveDuration(timeout, "enter");
        onEnter?.(host, isAppearing);

        if (ms <= 0) {
          node.style.transition = "";
          node.style.opacity = "";
          node.style.transform = "";
          node.style.willChange = "";
          onEntering?.(host, isAppearing);
          phase.current = "idle";
          onEntered?.(host, isAppearing);
          return;
        }

        node.style.willChange = "transform, opacity";
        if (!interruptedExit) {
          // Start pose: slightly shrunk and transparent, committed without a transition.
          node.style.transition = "none";
          node.style.opacity = "0";
          node.style.transform = `scale(${recipe.openScale})`;
        }
        // Popover positions the surface and sets transform-origin here.
        onEntering?.(host, isAppearing);
        // Flush the start pose so the transition below has a "from" value. A computed
        // *opacity* read is a style-only flush; offsetHeight would also force the layout
        // Popover just dirtied by writing top/left.
        void getComputedStyle(node).opacity;

        phase.current = "entering";
        node.style.transition = `opacity ${ms}ms ${openEase}, transform ${ms}ms ${openEase}`;
        node.style.opacity = "1";
        node.style.transform = "scale(1)";

        cancelPhase.current = afterTransition(node, ms, () => {
          // Rest state: no inline motion styles, no lingering compositor layer.
          node.style.willChange = "";
          node.style.transition = "";
          node.style.transform = "";
          node.style.opacity = "";
          phase.current = "idle";
          onEntered?.(host, isAppearing);
        });
        return;
      }

      // Mounted closed (never happens without keepMounted): nothing to animate out of.
      if (isAppearing) {
        return;
      }

      const ms = resolveDuration(timeout, "exit");
      onExit?.(host);

      if (ms <= 0) {
        onExiting?.(host);
        phase.current = "idle";
        onExited?.(host);
        return;
      }

      // No reset here: an exit that interrupts the open retargets from wherever the
      // open got to, which is what CSS transitions do natively.
      phase.current = "exiting";
      node.style.willChange = "transform, opacity";
      node.style.transition = `opacity ${ms}ms ${closeEase}, transform ${ms}ms ${closeEase}`;
      onExiting?.(host);
      node.style.opacity = "0";
      node.style.transform = `scale(${recipe.closeScale})`;

      cancelPhase.current = afterTransition(node, ms, () => {
        node.style.willChange = "";
        phase.current = "idle";
        // Modal unmounts the surface on this callback.
        onExited?.(host);
      });
      // Only `in` drives a new phase; the callbacks are fresh closures every render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inProp]);

    // Never call back into Modal/Popover after the surface is gone.
    useEffect(() => () => cancelPhase.current?.(), []);

    // Merge rather than replace `style`: Popover drives the paper's own style prop
    // (opacity: 0 until it has been positioned).
    const { style, ...rest } = other;
    return cloneElement(children, {
      ...rest,
      ...(style && { style: { ...style, ...children.props.style } }),
      ref: handleRef,
    });
  });
  ScaleTransition.displayName = displayName;
  return ScaleTransition;
};

// ── Recipes ───────────────────────────────────────────────────────────────────

// transitions-dev "Modal": grows from --scale-large (0.96) over 250ms, dips back to it over
// 150ms. Symmetric in scale — the modal leaves the way it came, just faster.
// The paper is the container's only child (DialogContainer renders DialogPaper alone), so it is
// the one that moves; the transparent container around it never gets a layer.
export const ModalTransition = createScaleTransition(
  {
    openMs: MODAL_OPEN_MS,
    closeMs: MODAL_CLOSE_MS,
    openScale: MODAL_SCALE,
    closeScale: MODAL_SCALE,
    target: (node) => node.firstElementChild as HTMLElement | null,
  },
  "ModalTransition"
);

// transitions-dev "Tooltip": a barely-there 0.98 grow on ease-out, and an out so short it reads
// as instant. Pair it with `enterDelay={TOOLTIP_DELAY_MS}` for the recipe's intent delay.
export const TooltipTransition = createScaleTransition(
  {
    openMs: TOOLTIP_IN_MS,
    closeMs: TOOLTIP_OUT_MS,
    openScale: TOOLTIP_SCALE,
    closeScale: TOOLTIP_SCALE,
    openEase: EASE_OUT,
    closeEase: EASE_OUT,
  },
  "TooltipTransition"
);

// Spread onto a Dialog. `transitionDuration` is passed as well as the component so the backdrop
// fades on the same clock as the paper (Dialog hands it to both), and drops to 0 with it.
export const modalTransitionProps = {
  TransitionComponent: ModalTransition,
  transitionDuration: prefersReducedMotion ? 0 : { enter: MODAL_OPEN_MS, exit: MODAL_CLOSE_MS },
} as const;

// Spread onto a Tooltip.
export const tooltipTransitionProps = {
  TransitionComponent: TooltipTransition,
  TransitionProps: { timeout: prefersReducedMotion ? 0 : { enter: TOOLTIP_IN_MS, exit: TOOLTIP_OUT_MS } },
} as const;

// Spread onto the bottom-sheet SwipeableDrawer every popup uses on a phone. MUI's Slide already
// moves the paper with transform alone; this only retimes it as a panel (400ms in, 350ms out)
// and swaps its curves for the project's — the entrance curve in, --ease-smooth-out out.
export const sheetTransitionProps = {
  transitionDuration: prefersReducedMotion ? 0 : { enter: SHEET_OPEN_MS, exit: SHEET_CLOSE_MS },
  SlideProps: { easing: { enter: EASE_ENTRANCE, exit: EASE_SMOOTH_OUT } },
} as const;

export default ModalTransition;
